import React from 'react';
import PublicHeader from './PublicHeader';
import { COLOURING_PAGE, COLOURING_SHEETS, isColouringCompOpen } from '../content/colouringComp';

interface Props {
  onNavigateHome: () => void;
}

/**
 * /colouring-competition: the two printable colouring sheets for the junior
 * school-holidays competition. Static, public, no backend. The PDFs are served
 * from frontend/public/colouring/ (same approach as the club Constitution PDF).
 */
const ColouringCompPage: React.FC<Props> = ({ onNavigateHome }) => {
  const open = isColouringCompOpen();
  return (
    <main className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <PublicHeader title="Colouring Competition" />

      <div className="max-w-5xl mx-auto pt-6 px-4">
        <button type="button" onClick={onNavigateHome} className="text-sm text-fishing-blue hover:underline">
          ← Back to Home
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1d2e61] mb-4">{COLOURING_PAGE.heading}</h1>

        {!open && (
          <p role="status" className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-4 mb-6">
            This competition has closed. The sheets are still here to download and enjoy.
          </p>
        )}

        <p className="text-gray-700 text-lg leading-relaxed mb-8 max-w-3xl">{COLOURING_PAGE.intro}</p>

        <section aria-labelledby="how-to-enter" className="bg-white rounded-2xl shadow p-6 mb-10 max-w-3xl">
          <h2 id="how-to-enter" className="text-xl font-bold text-gray-900 mb-3">
            {COLOURING_PAGE.howToEnterHeading}
          </h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            {COLOURING_PAGE.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <p className="text-sm text-gray-600 mt-4">{COLOURING_PAGE.closingNote}</p>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {COLOURING_SHEETS.map((sheet) => (
            <figure key={sheet.id} className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
              <picture>
                <source type="image/webp" srcSet={`${sheet.webp640} 640w, ${sheet.webp1200} 1200w`} sizes="(min-width: 768px) 50vw, 100vw" />
                <img
                  src={sheet.jpg}
                  alt={sheet.alt}
                  width={sheet.width}
                  height={sheet.height}
                  decoding="async"
                  className="block w-full h-auto border-b border-gray-100"
                />
              </picture>
              <figcaption className="p-5 flex flex-wrap items-center justify-between gap-3">
                <span className="font-semibold text-gray-900">{sheet.title}</span>
                <a
                  href={sheet.pdf}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${COLOURING_PAGE.downloadLabel}: ${sheet.title}`}
                  className="bg-fishing-blue hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-fishing-blue"
                >
                  {COLOURING_PAGE.downloadLabel}
                </a>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </main>
  );
};

export default ColouringCompPage;
